import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfigService } from 'providers/config.service';
import { EnvironmentService } from 'providers/environment.service';

export interface BetaLinkState {
    isChargEye: boolean;
    skipDialog: boolean;
    betaLink: string;
}

@Injectable({
    providedIn: 'root',
})
export class BetaLinkService {
    private readonly STORAGE_KEY = 'hide-beta-update-dialog';
    private readonly baseUrl: string;
    public betaLink: string;

    constructor(
        private readonly config: ConfigService,
        private readonly environmentService: EnvironmentService,
        private readonly dialog: MatDialog
    ) {
        this.baseUrl = this.config?.config.apiBaseUrl || '';
        this.betaLink = `https://${this.baseUrl}/2.0/?=beta:true`;
    }

    /**
     * Get beta link state as an observable
     * Returns information about whether to show the beta link and skip the dialog
     */
    getBetaLinkState$(): Observable<BetaLinkState> {
        return this.environmentService.getAppOrigin$().pipe(
            map((origin) => {
                const isChargEye = origin === 'chargeye';
                const skipDialog = this.getSkipDialogPreference();

                return {
                    isChargEye,
                    skipDialog,
                    betaLink: this.betaLink,
                };
            })
        );
    }

    /**
     * Get the skip dialog preference from localStorage
     */
    getSkipDialogPreference(): boolean {
        return localStorage.getItem(this.STORAGE_KEY) === 'true';
    }

    /**
     * Set the skip dialog preference in localStorage
     */
    setSkipDialogPreference(skip: boolean): void {
        localStorage.setItem(this.STORAGE_KEY, skip.toString());
    }

    /**
     * Navigate to the beta version
     * @param redirect - If true, immediately redirect. If false, show dialog first.
     * @param dialogComponent - The dialog component to open (optional, for avoiding circular deps)
     * @param destinationUrl - Optional custom URL to navigate to instead of the default beta link
     */
    navigateToBeta(redirect?: boolean, dialogComponent?: any, destinationUrl?: string): void {
        const url = destinationUrl ?? this.betaLink;
        if (redirect) {
            globalThis.location.href = url;
            return;
        }
        this.openDialog(dialogComponent, url);
    }

    /**
     * Open the beta notification dialog
     * @param dialogComponent - The dialog component class to open
     * @param destinationUrl - URL to navigate to when user confirms
     */
    openDialog(dialogComponent?: any, destinationUrl?: string): void {
        const url = destinationUrl ?? this.betaLink;
        const skipDialog = this.getSkipDialogPreference();
        if (skipDialog) {
            globalThis.location.href = url;
            return;
        }

        if (!dialogComponent) {
            console.error('Dialog component not provided to BetaLinkService.openDialog');
            return;
        }

        const dialogRef = this.dialog.open(dialogComponent);

        dialogRef.afterClosed().subscribe((result) => {
            if (result?.doNotShow) {
                this.setSkipDialogPreference(true);
            }
            if (result?.gotoBeta) {
                globalThis.location.href = url;
            }
        });
    }
}
